import {
	Dialog,
	FormattedText,
	SearchInputForm,
	Toolbar,
	TransparentButtonList,
	Typography,
} from "client/Components";
import { normalizeForSearch } from "client/Data/searching";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation,
} from "client/Hooks";
import { getClientId, hasClientId } from "client/Misc/clientId";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration,
} from "client/Validation";
import {
	isEmailAddress,
	isNotEmpty,
	isNotNullNorUndefined,
} from "client/Validation/rules";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { MailboxesOrderableList } from "./MailboxesOrderableList";
import {
	AvailableMailboxesListModel,
	AvailableMailboxSaveModel,
	AvailableMailboxesListSaveModel,
	getConfiguredMailboxes,
	MailboxType,
	AvailableMailboxModel,
	MainMailboxSaveModel,
	SharedMailboxSaveModel,
	saveMailboxes,
} from "client/Api/Email/Mailboxes/mailboxes";
import styles from "./MailboxesManagementSidebar.module.less";
import { newGuid } from "client_legacy/ndaw/guid";

const validationConfiguration: IValidationConfiguration<AvailableMailboxesListSaveModel> =
	createValidationConfiguration<AvailableMailboxesListSaveModel>();

validationConfiguration
	.forCollection("mailboxes")
	.forItems((item) =>
		hasClientId(item) ? getClientId(item) : item.id.toString()
	)
	.whenType(m => m.type === MailboxType.Manual)
	.forScalar("emailAddress")
	.add(
		isEmailAddress().validateOn("formSubmit")
			.fixErrorOn("inputFinished"),
		isNotNullNorUndefined().validateOn("formSubmit")
			.fixErrorOn("inputFinished"),
		isNotEmpty().validateOn("formSubmit")
			.fixErrorOn("inputFinished")
	);

export type MailboxesManagementSidebarProps = {
	onClose: () => void;
	onSave: () => void;
};

export const MailboxesManagementSidebar = (
	props: MailboxesManagementSidebarProps
): ReactElement => {
	const { onClose, onSave } = props;
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({ value: "", version: newGuid() });
	const [mainEmail, setMainEmail] = useState<string>("");
	const [formValue, setFormValue] = useState<AvailableMailboxesListSaveModel>(
		{
			mailboxes: [],
		}
	);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const formHandler: IFormHandler<AvailableMailboxesListSaveModel> = useFormHandler(
		formValue,
		setFormValue,
		validationConfiguration
	);

	const hideAll = useCallback(
		(): void => {
			formHandler.for("mailboxes")
				.forEach((itemHandler) => {
					if(itemHandler.value.type === MailboxType.Manual){
						itemHandler.for("isVisible")
							.reducer
							.setValue(false);
					}
				});
		},
		[formHandler]
	);

	const showAll = useCallback(
		(): void => {
			formHandler.for("mailboxes")
				.forEach((itemHandler) => {
					if(itemHandler.value.type === MailboxType.Manual){
						itemHandler.for("isVisible")
							.reducer
							.setValue(true);
					}
				});
		},
		[formHandler]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			try {
				setIsBusy(true);
				const validationResult: InputValidationResult =	await formHandler.validation
					.validate(
						globalCancellation
					);

				if (validationResult.kind === InputValidationResultKind.Invalid) {
					setIsBusy(false);
					return;
				}

				await saveMailboxes(formValue, globalCancellation);
				alertManager.showSuccess(localize("_ChangesSaved"));

				onSave();
				onClose();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}
				setIsBusy(false);
				if (formHandler.validation.tryProcessServerError(e)) {
					return;
				}
				alertManager.showError(localize("_ChangesNotSaved"));
			}
		},
		[
			alertManager,
			formHandler.validation,
			formValue,
			onClose,
			onSave,
			globalCancellation
		]
	);

	const normalizedSearchTerm: string = useMemo(
		() => normalizeForSearch(searchTerm.value),
		[searchTerm]
	);

	useEffect(
		() => {
			async function fetchConfiguredMailboxes(
				signal: AbortSignal
			): Promise<void> {
				try {
					setIsBusy(true);
					const mailboxList: AvailableMailboxesListModel = await getConfiguredMailboxes(signal);

					const saveModels: readonly AvailableMailboxSaveModel[] = mailboxList.mailboxes
						.map(
							(availableMailbox: AvailableMailboxModel) => {
								switch (availableMailbox.type) {
									case MailboxType.Main:
										const { emailAddress, ...rest } = availableMailbox;
										setMainEmail(emailAddress);
										const mainMailboxSaveModel: MainMailboxSaveModel = {
											...rest,
											type: availableMailbox.type,
										};

										return mainMailboxSaveModel;
									case MailboxType.Manual:
										const sharedMailboxSaveModel: SharedMailboxSaveModel = {
											...availableMailbox,
											type: availableMailbox.type,
										};
										return sharedMailboxSaveModel;

									default:
										return never(availableMailbox.type);
								}
							}
						);
					const sortedMailboxList: AvailableMailboxesListSaveModel = {
						mailboxes: sortBy(
							saveModels,
							(m) => m.sortOrder
						),
					};

					setIsBusy(false);
					setFormValue(sortedMailboxList);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			}

			const cancellation = new AbortController();
			fetchConfiguredMailboxes(cancellation.signal);

			return () => {
				cancellation.abort();
			};
		},
		[alertManager, onClose]
	);

	return (
		<Dialog
			trackingId="mailboxes-management-left-sidebar"
			hasBusyIndicator={isBusy}
			variant="left-slider"
			onClose={onClose}
		>
			<Dialog.Header
				title={localize("_ManageMailboxes")}
				onClose={onClose}
				hasBorder
			/>
			<Dialog.Body>
				<p>
					{localize("_HereYouCanDetermineTheOrderAndVisibilityOfTheMailboxes")}
				</p>
				<SearchInputForm
					value={searchTerm}
					onChange={(value, version) => setSearchTerm({ value, version })}
					trackingId="search-term"
					placeholder={(
						<FormattedText
							text={localize("_FilterByProperties")}
							thingsToReplace={[
								{
									what: "{{Properties}}",
									filler: (
										<Typography weight="bold" isItalic>
											{localize("_Mailboxes")}
										</Typography>
									)
								}
							]}
						/>
					)}
					hasMarginBottom
					height="large"
					icon="filter"
				/>
				<div className={styles.transparentButtonListContainer}>
					<TransparentButtonList>
						<TransparentButtonList.Item
							icon="eye"
							text={localize("_ShowAllMailboxes")}
							onClick={showAll}
							trackingId="show-all"
						/>
						<TransparentButtonList.Item
							icon="eye-slash"
							text={localize("_HideAllMailboxes")}
							onClick={hideAll}
							trackingId="hide-all"
						/>
					</TransparentButtonList>
				</div>
				<MailboxesOrderableList
					mailboxesHandler={formHandler.for("mailboxes")}
					mainEmail={mainEmail}
					searchTerm={normalizedSearchTerm}
					onItemAdded={() => setSearchTerm({ value: "", version: newGuid() })}
				/>
			</Dialog.Body>
			<Dialog.Footer hasBorder>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Save")}
						onClick={saveHandler}
						trackingId="save"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
MailboxesManagementSidebar.displayName = "MailboxesManagementSidebar";
